/** Size of the SVG canvas - large enough to feel infinite when panning */
export const CANVAS_SIZE = 100000;

/** Offset to center the actual map content within the canvas */
export const MAP_OFFSET = {
    x: 1900,
    y: 1900,
} as const;

/** Default zoom level (scale factor) */
export const DEFAULT_SCALE = 0.18;
export const MIN_SCALE = 0.1;
export const MAX_SCALE = 3;

/** Zoom intensity per wheel/trackpad tick */
export const ZOOM_INTENSITY = 0.02;

/** Approximate bounding size of all map content in SVG units */
export const MAP_CONTENT_SIZE = {
    width: 4500,
    height: 4000,
} as const;

/** Minimum screen pixels of map content that must remain visible at each edge */
export const BOUNDARY_MARGIN = 200;

export type Position = {
    x: number;
    y: number;
};

export type MapState = {
    position: Position;
    scale: number;
};

export type ContainerSize = {
    width: number;
    height: number;
};

/**
 * Calculate the initial map position that centers the map content in the viewport.
 *
 * @param containerSize - The size of the visible container
 * @param mapOffset - Offset of the map content from 0,0
 * @param scale - Initial scale factor
 * @returns Initial position in map coordinates
 */
export function getInitialPosition(containerSize: ContainerSize, mapOffset: Position, scale: number): Position {
    return {
        x: containerSize.width / 2 - mapOffset.x * scale,
        y: containerSize.height / 2 - mapOffset.y * scale,
    };
}

/**
 * Calculate new map state after a zoom operation centered on a specific point.
 * This keeps the point under the cursor fixed while zooming.
 *
 * @param currentState - Current map state (position and scale)
 * @param mousePos - Mouse position in screen space (relative to SVG element)
 * @param zoomDelta - Amount to zoom (positive = zoom in, negative = zoom out)
 * @returns New map state with updated position and scale
 */
export function applyZoom(currentState: MapState, mousePos: Position, zoomDelta: number): MapState {
    const { position: pan, scale } = currentState;

    // Calculate new scale within limits
    const scaleMultiplier = zoomDelta < 0 ? 1 + ZOOM_INTENSITY : 1 - ZOOM_INTENSITY;
    const newScale = Math.min(Math.max(scale * scaleMultiplier, MIN_SCALE), MAX_SCALE);

    // If scale didn't change (hit limits), return current state
    if (newScale === scale) {
        return currentState;
    }

    // Take mouse position into account to keep it stable during zoom
    const newPosition: Position = {
        x: mousePos.x - ((mousePos.x - pan.x) * newScale) / scale,
        y: mousePos.y - ((mousePos.y - pan.y) * newScale) / scale,
    };

    return {
        position: newPosition,
        scale: newScale,
    };
}

/**
 * Calculate new map position after a pan operation.
 *
 * @param currentState - Current map state
 * @param screenDelta - Movement in screen pixels
 * @returns New map state with updated position
 */
export function applyPan(currentState: MapState, screenDelta: Position): MapState {
    const { position, scale } = currentState;

    // Convert screen space movement to map space movement
    const mapDelta: Position = {
        x: screenDelta.x,
        y: screenDelta.y,
    };

    return {
        position: {
            x: position.x + mapDelta.x,
            y: position.y + mapDelta.y,
        },
        scale,
    };
}

/**
 * Clamp a value between min and max.
 */
export function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

/**
 * Clamp a position so that map content remains visible within the container.
 * At least BOUNDARY_MARGIN pixels of content must be visible at each edge.
 */
export function clampPosition(position: Position, containerSize: ContainerSize, scale: number): Position {
    return {
        x: clamp(position.x, BOUNDARY_MARGIN - MAP_CONTENT_SIZE.width * scale, containerSize.width - BOUNDARY_MARGIN),
        y: clamp(position.y, BOUNDARY_MARGIN - MAP_CONTENT_SIZE.height * scale, containerSize.height - BOUNDARY_MARGIN),
    };
}
